import { useMemo } from 'react';
import MojitoModules from 'mojito/modules';

const { useCardsPromotions } = MojitoModules.EventCardsCarousel.Hooks;
const {
    useEventItems,
    useEventsChunkRequest,
    useEventsLoadDone,
    useMarketItems,
    useMarketsChunkRequest,
    useMarketsLoadDone,
} = MojitoModules.Common.Hooks;
const { usePromotions, usePromotionsLoadDone } = MojitoModules.Promotions.hooks;

const isSupportedMainEventCard = card => card.content?.eventId && card.content?.marketId;

/**
 * Contains custom hooks used by the search discovery view.
 *
 * @class SearchDiscoveryViewHooks
 * @name SearchDiscoveryViewHooks
 * @memberof Mojito.Applications.Sports.Search
 */

/**
 * Collects the promotion cards for the given location/page and turns the ones bound to a single event
 * or market into the card infos rendered by the search discovery main-events list. The referenced
 * events and markets are chunk-subscribed so their data is available, and a combined pending state is
 * reported while promotions, events or markets are still loading.
 *
 * @function useMainEventsFromPromotions
 *
 * @param {object} config - Promotions configuration.
 * @param {string} config.locationId - Promotions location id.
 * @param {string} [config.pageId] - Applicable page filter; omit to include promotions from every page.
 * @param {{value: string}} config.priorityType - Card priority type used to pick the promotion cards.
 * @param {number} [config.maxCardsAmount] - Maximum number of visible cards to render. The cap is applied after the AVAILABLE filter so cards whose event or market resolved unavailable do not count toward the limit; omit to render every renderable card.
 * @param {string} [instanceId] - Owner instance id used to scope the chunk subscriptions.
 *
 * @returns {{pending: boolean, cardInfos: Array<{id: string, eventId: string, marketId: string}>}} Pending flag and the renderable card infos.
 * @memberof Mojito.Applications.Sports.Search.SearchDiscoveryViewHooks
 */
export const useMainEventsFromPromotions = (config, instanceId) => {
    const { locationId, pageId, priorityType, maxCardsAmount } = config;
    const promotions = usePromotions(locationId, pageId);
    const promotionsLoaded = usePromotionsLoadDone();
    const cards = useCardsPromotions(promotions, priorityType.value);

    const cardInfos = useMemo(
        () =>
            cards.filter(isSupportedMainEventCard).map(card => ({
                id: card.id,
                eventId: card.content.eventId,
                marketId: card.content.marketId,
            })),
        [cards]
    );

    const eventIds = useMemo(() => cardInfos.map(cardInfo => cardInfo.eventId), [cardInfos]);
    const marketIds = useMemo(() => cardInfos.map(cardInfo => cardInfo.marketId), [cardInfos]);

    useEventsChunkRequest(eventIds, instanceId);
    useMarketsChunkRequest(marketIds, instanceId);

    const eventsLoadDone = useEventsLoadDone(eventIds);
    const marketsLoadDone = useMarketsLoadDone(marketIds);
    const pending =
        !promotionsLoaded || (cardInfos.length > 0 && (!eventsLoadDone || !marketsLoadDone));

    // Slice after the AVAILABLE filter so `maxCardsAmount` counts visible cards.
    const events = useEventItems(eventIds, false);
    const markets = useMarketItems(marketIds, false);
    const renderableCardInfos = useMemo(() => {
        const availableEventIds = new Set(events.map(event => event.id));
        const availableMarketIds = new Set(markets.map(market => market.id));
        return cardInfos
            .filter(
                cardInfo =>
                    availableEventIds.has(cardInfo.eventId) &&
                    availableMarketIds.has(cardInfo.marketId)
            )
            .slice(0, maxCardsAmount);
    }, [cardInfos, events, markets, maxCardsAmount]);

    return { pending, cardInfos: renderableCardInfos };
};
